// =============================================
// Parent Settings Page
// Kipikid - מערכת נוכחות ילדים בגנים
// =============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User,
  Bell,
  BellRing,
  Shield,
  Globe,
  HelpCircle,
  LogOut,
  ChevronLeft,
  Phone,
  Mail,
  Camera,
  Moon,
  Sun,
  MessageSquare,
  Volume2,
  KeyRound
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import ChangePassword from '../../components/ChangePassword';
import { isPushSupported, isSubscribedToPush, subscribeToPush, unsubscribeFromPush, getNotificationPermission } from '../../services/pushNotifications';
import { parentColors } from '../../styles/colors';

// Colors
const colors = {
  ...parentColors,
  background: '#f9fafb',
};

export default function ParentSettings() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported] = useState(isPushSupported());

  // בדיקת מצב Push notification בטעינה
  useEffect(() => {
    if (pushSupported) {
      isSubscribedToPush().then(setPushEnabled);
    }
  }, [pushSupported]);

  const handlePushToggle = async () => {
    if (pushEnabled) {
      await unsubscribeFromPush();
      setPushEnabled(false);
    } else {
      const success = await subscribeToPush();
      setPushEnabled(success);
      if (!success && getNotificationPermission() === 'denied') {
        alert('ההתראות חסומות בדפדפן. יש לפתוח את הגדרות הדפדפן ולאשר התראות.');
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/parent/login');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Profile Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Avatar */}
          <div style={{ position: 'relative' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '24px',
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '28px',
              fontWeight: 'bold',
            }}>
              {user?.firstName?.[0] || 'U'}
            </div>
            <motion.button
              style={{
                position: 'absolute',
                bottom: '-4px',
                right: '-4px',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: colors.secondary,
                border: '3px solid white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
              whileTap={{ scale: 0.9 }}
            >
              <Camera size={14} color="white" />
            </motion.button>
          </div>

          {/* Info */}
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: colors.text, marginBottom: '4px' }}>
              {user?.firstName} {user?.lastName}
            </h2>
            <p style={{ fontSize: '14px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Phone size={14} />
              {user?.phone || '050-1234567'}
            </p>
          </div>
        </div>

        {/* Edit Profile Button */}
        <motion.button
          style={{
            width: '100%',
            marginTop: '16px',
            padding: '12px',
            backgroundColor: colors.primaryLight,
            color: colors.primary,
            border: 'none',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
          }}
          whileTap={{ scale: 0.98 }}
        >
          עריכת פרופיל
        </motion.button>
      </motion.div>

      {/* Settings Groups */}
      <SettingsGroup title="העדפות">
        <SettingItem
          icon={Bell}
          iconColor={colors.primary}
          iconBg={colors.primaryLight}
          title="התראות"
          subtitle="ניהול התראות והודעות"
          onClick={() => {}}
        />
        <SettingItem
          icon={isDarkMode ? Moon : Sun}
          iconColor={colors.secondary}
          iconBg={colors.secondaryLight}
          title="מצב תצוגה"
          subtitle={isDarkMode ? 'מצב כהה' : 'מצב בהיר'}
          toggle
          isToggled={isDarkMode}
          onToggle={() => setIsDarkMode(!isDarkMode)}
        />
        <SettingItem
          icon={Globe}
          iconColor="#6366f1"
          iconBg="#eef2ff"
          title="שפה"
          subtitle="עברית"
          onClick={() => {}}
        />
      </SettingsGroup>

      <SettingsGroup title="התראות">
        {pushSupported && (
          <SettingItem
            icon={BellRing}
            iconColor="#8b5cf6"
            iconBg="#ede9fe"
            title="Push התראות"
            subtitle={pushEnabled ? "התראות Push מופעלות" : "הפעל התראות Push בדפדפן"}
            toggle
            isToggled={pushEnabled}
            onToggle={handlePushToggle}
          />
        )}
        <SettingItem
          icon={MessageSquare}
          iconColor={colors.secondary}
          iconBg={colors.secondaryLight}
          title="הודעות מהגן"
          subtitle="קבלת הודעות מהגננת"
          toggle
          isToggled={true}
          onToggle={() => {}}
        />
        <SettingItem
          icon={Volume2}
          iconColor={colors.primary}
          iconBg={colors.primaryLight}
          title="צלילי התראה"
          subtitle="צליל בעת קבלת התראה"
          toggle
          isToggled={true}
          onToggle={() => {}}
        />
      </SettingsGroup>

      <SettingsGroup title="כללי">
        <SettingItem
          icon={KeyRound}
          iconColor={colors.primary}
          iconBg={colors.primaryLight}
          title="שינוי סיסמה"
          subtitle="עדכון סיסמת הכניסה"
          onClick={() => setShowChangePassword(true)}
        />
        <SettingItem
          icon={Shield}
          iconColor="#22c55e"
          iconBg="#dcfce7"
          title="פרטיות ואבטחה"
          subtitle="ניהול הגדרות פרטיות"
          onClick={() => {}}
        />
        <SettingItem
          icon={HelpCircle}
          iconColor="#8b5cf6"
          iconBg="#ede9fe"
          title="עזרה ותמיכה"
          subtitle="שאלות נפוצות ויצירת קשר"
          onClick={() => {}}
        />
      </SettingsGroup>

      {/* Logout Button */}
      <motion.button
        onClick={handleLogout}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          width: '100%',
          padding: '16px',
          backgroundColor: colors.dangerLight,
          color: colors.danger,
          border: 'none',
          borderRadius: '16px',
          fontSize: '16px',
          fontWeight: '500',
          cursor: 'pointer',
        }}
        whileTap={{ scale: 0.98 }}
      >
        <LogOut size={20} />
        התנתקות
      </motion.button>

      {/* Version */}
      <p style={{ textAlign: 'center', fontSize: '12px', color: colors.textLight }}>
        Kipikid גרסה 1.0.0
      </p>

      {/* Change Password Modal */}
      <AnimatePresence>
        {showChangePassword && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px',
            }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) setShowChangePassword(false); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                background: 'white',
                borderRadius: '20px',
                padding: '24px',
                maxWidth: '400px',
                width: '100%',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              }}
            >
              <ChangePassword
                mode="voluntary"
                onSuccess={() => setShowChangePassword(false)}
                onCancel={() => setShowChangePassword(false)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Settings Group Component
function SettingsGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 style={{ 
        fontSize: '13px', 
        fontWeight: '600', 
        color: colors.textLight, 
        marginBottom: '12px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}>
        {title}
      </h3>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}>
        {children}
      </div>
    </div>
  );
}

// Setting Item Component
interface SettingItemProps {
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  title: string;
  subtitle: string;
  toggle?: boolean;
  isToggled?: boolean;
  onToggle?: () => void;
  onClick?: () => void;
}

function SettingItem({ 
  icon: Icon, 
  iconColor, 
  iconBg, 
  title, 
  subtitle, 
  toggle, 
  isToggled, 
  onToggle,
  onClick 
}: SettingItemProps) {
  return (
    <motion.div
      onClick={toggle ? onToggle : onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px',
        borderBottom: `1px solid ${colors.border}`,
        cursor: 'pointer',
      }}
      whileTap={{ backgroundColor: '#f9fafb' }}
    >
      {/* Icon */}
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        backgroundColor: iconBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Icon size={20} color={iconColor} />
      </div>

      {/* Text */}
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: '15px', fontWeight: '500', color: colors.text }}>{title}</p>
        <p style={{ fontSize: '13px', color: colors.textLight }}>{subtitle}</p>
      </div>

      {/* Action */}
      {toggle ? (
        <div style={{
          width: '50px',
          height: '28px',
          borderRadius: '14px',
          backgroundColor: isToggled ? colors.secondary : colors.border,
          position: 'relative',
          transition: 'background-color 0.2s',
        }}>
          <motion.div
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '12px',
              backgroundColor: 'white',
              position: 'absolute',
              top: '2px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
            animate={{ left: isToggled ? '24px' : '2px' }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          />
        </div>
      ) : (
        <ChevronLeft size={20} color={colors.textLight} />
      )}
    </motion.div>
  );
}